<template>
  <div>
  <ValidationProvider tag='div' :rules='rules' :name='vid' :label='label || name' :mode='mode' :model-value='modelValue' :validate-on-mount='false' :validate-on-model-update='validateOnModelUpdate' v-slot='{ errors }'>
    <b-form-group :label-for='name || labelText' :label='labelText' :class='{ "compact-control": compactFormat }'>
      <div class='date-container' ref='dateContainer'>
        <b-row>
            <b-col>
            <b-input-group>
                <input
                ref='dateInput'
                :class='{ "is-invalid": errors.length, "form-control" : true}'
                :value='displayValue'
                @change='onManualInput($event.target.value)'
                @click='toggleCalendar'
                @keydown='showCalendar = false'
                :disabled='disabled'
                :placeholder="disabled ? '' : 'MM/DD/YYYY'" />
                <b-button v-if='!disabled'
                    class='px-0'
                    size='md'
                    :disabled='disabled'
                    @click='clearInput'>
                    <b-icon-x style="font-size: 1.15rem;"></b-icon-x>
                </b-button>
                <b-input-group-text class="px-0" v-if='infoMessage' :title="infoMessage">
                    <b-icon-info-circle></b-icon-info-circle>
                </b-input-group-text>
            </b-input-group>
            <Teleport to='body'>
                <div v-if='showCalendar' class='calendar-popup' ref='calendarPopup' :style='popupStyle'>
                    <calendar
                        :initial-page="calendarInitialPage"
                        @dayclick='onDayClick($event)'
                    />
                </div>
            </Teleport>
            </b-col>
        </b-row>
        <span class="text-danger">{{ errors[0] }}</span>
        </div>
    </b-form-group>
  </ValidationProvider>
  </div>
</template>

<script>
import baseInputs from '@/components/mixins/BaseInputs';
import validatedInput from '@/components/mixins/ValidatedInput';
import 'v-calendar/style.css';
import { Calendar } from 'v-calendar';
import momentTz from 'moment-timezone';
import { useSelectListsStore } from '@/stores/selectLists.js';

export default {
    name: 'p-datepicker',
    mixins: [baseInputs, validatedInput],
    emits: ['update:modelValue', 'input', 'date-picker-initialized', 'end-of-selected-day'],
    components: { Calendar },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        compactFormat:{
            type: Boolean,
            default: false
        },
        startDate: {
            type: Date,
            default: function() { return new Date(1900, 0, 1) }
        },
        isEndDate: {
            type: Boolean,
            default: false
        },
        // Validate on each value change (pick/clear) so the inline error shows
        // while editing, matching p-input. Without this, the modelValue syncs
        // into the field silently (meta.valid only, no errors) and required /
        // date-comparison errors surface only on form submit. Overridable per-usage.
        validateOnModelUpdate: {
            type: Boolean,
            default: true
        }
    },
    data(){
        return {
            displayValue: '',
            showCalendar: false,
            popupStyle: {}
        }
    },
    watch: {
        // Process the CURRENT modelValue. This watcher's immediate run happens before the `value`
        // watcher below has had a chance to cache anything, so reading a cache here would process
        // a stale null and — because processValue(null) re-emits when this.value is set — fire
        // update:modelValue=null straight back over a non-null initial value, wiping restored or
        // defaulted dates whenever the store timezone was already loaded at mount.
        storeTimeZone: {
            immediate: true,
            handler(newValue){
                if(newValue) {
                    this.processValue(this.value);
                }
            }
        },
        value: {
            immediate: true,
            handler(newValue) {
                this.processValue(newValue);
            }
        }
    },
    computed: {
        value() { return this.modelValue; },
        calendarInitialPage() {
            if (this.displayValue) {
                const parts = this.displayValue.split('/');
                if (parts.length === 3) {
                    return { month: parseInt(parts[0]), year: parseInt(parts[2]) };
                }
            }
            const now = new Date();
            return { month: now.getMonth() + 1, year: now.getFullYear() };
        },
        storeTimeZone() {
            return useSelectListsStore().lists.storeTimeZone ?? '';
        }
    },    
    methods: {
        handleInput(value) {
            this.$emit('update:modelValue', value);
            this.$emit('input', value);
            // Companion event: 23:59:59.997 of the same day as the selected start
            // (one day minus 3ms past the start ISO). Useful for single-day range filters.
            const endOfDay = value == null
                ? null
                : new Date(new Date(value).getTime() + 86400000 - 3).toISOString();
            this.$emit('end-of-selected-day', endOfDay);
        },
        processValue(newValue) {
            if (!this.storeTimeZone) return;

            if (!newValue) {
                this.displayValue = '';
                if (this.value !== null && this.value !== undefined) {
                    this.handleInput(null);
                }
                return;
            }

            let date;
            if (typeof newValue === 'string') {
                date = new Date(newValue);
            } else if (typeof newValue === 'object' && newValue.date) {
                date = newValue.date;
            } else {
                date = newValue;
            }

            const storeMoment = momentTz.utc(date).tz(this.storeTimeZone);
            const formatted = storeMoment.format('MM/DD/YYYY');

            if (this.displayValue !== formatted) {
                this.displayValue = formatted;
            }

            const converted = (!this.isEndDate
                ? storeMoment.startOf('day')
                : storeMoment.endOf('day').subtract(3, 'milliseconds')
            ).toISOString();

            // Only re-emit when the converted value differs from the current model.
            // Otherwise the value watcher re-fires processValue → recursive update.
            if (converted !== this.value) {
                this.handleInput(converted);
            }
        },
        onDayClick(day) {
            // day.date is a JS Date in local browser time
            // Treat the selected date as if it's in the store's timezone
            const storeMoment = momentTz.tz(
                { year: day.year, month: day.month - 1, day: day.day },
                this.storeTimeZone
            );
            this.displayValue = storeMoment.format('MM/DD/YYYY');

            const converted = (!this.isEndDate
                ? storeMoment.startOf('day')
                : storeMoment.endOf('day').subtract(3, 'milliseconds')
            ).toISOString();

            this.handleInput(converted);
            this.showCalendar = false;
        },
        onManualInput(inputStr) {
            if (!inputStr) {
                this.clearInput();
                return;
            }

            const parsed = momentTz.tz(inputStr, 'MM/DD/YYYY', this.storeTimeZone);
            if (parsed.isValid()) {
                this.displayValue = parsed.format('MM/DD/YYYY');
                const converted = (!this.isEndDate
                    ? parsed.startOf('day')
                    : parsed.endOf('day').subtract(3, 'milliseconds')
                ).toISOString();
                this.handleInput(converted);
            } else {
                this.clearInput();
                // Force the input element to re-sync from displayValue ('') even
                // though :value didn't change reactively — the browser still shows
                // the user's invalid text otherwise.
                if (this.$refs.dateInput) this.$refs.dateInput.value = '';
            }
        },
        clearInput() {
            this.displayValue = '';
            this.handleInput(null);
            this.showCalendar = false;
        },
        toggleCalendar() {
            if (this.disabled) return;
            if (this.showCalendar) {
                this.showCalendar = false;
                return;
            }
            this.updatePopupPosition();
            this.showCalendar = true;
        },
        updatePopupPosition() {
            const inputEl = this.$refs.dateInput;
            if (!inputEl) return;
            const rect = inputEl.getBoundingClientRect();
            const calendarHeight = 280; // approx; v-calendar default height
            const fitsBelow = rect.bottom + calendarHeight <= window.innerHeight;
            this.popupStyle = {
                position: 'fixed',
                left: rect.left + 'px',
                top: (fitsBelow ? rect.bottom + 2 : rect.top - calendarHeight - 2) + 'px'
            };
        },
        onClickOutside(event) {
            if (!this.showCalendar) return;
            // Close unless the click is on the input itself or inside the (teleported) popup.
            if (this.$el.contains(event.target)) return;
            const popup = this.$refs.calendarPopup;
            if (popup && popup.contains(event.target)) return;
            this.showCalendar = false;
        },
        onWindowScrollOrResize() {
            if (this.showCalendar) this.updatePopupPosition();
        }
    },
    mounted() {
        document.addEventListener('click', this.onClickOutside);
        window.addEventListener('scroll', this.onWindowScrollOrResize, true);
        window.addEventListener('resize', this.onWindowScrollOrResize);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onClickOutside);
        window.removeEventListener('scroll', this.onWindowScrollOrResize, true);
        window.removeEventListener('resize', this.onWindowScrollOrResize);
    },
    created() {
        this.processValue(this.value);
        this.$nextTick(() => {
            this.$emit('date-picker-initialized');
        });
    }
};
</script>
<style scoped lang='scss'>
@import '@/styles/app/common/variables.scss';
:deep(.form-control:not([disabled])) {
    background-color: white;
}
:deep(.form-control[disabled]) {
    background-color: #edf1f2;
}
:deep(.input-group .form-control) {
    border-right: 0 !important;
}
:deep(.input-group .form-control[disabled]) {
    border-right: 1px solid #7D93A7 !important;
}
:deep(.date-container) {
    min-width: 90px;
    position: relative;
}
:deep(input::-ms-clear) {
  display: none;
}
:deep(.btn) {
    background-color: white !important;
    border: 1px solid #7D93A7 !important;
    border-left: 1px solid #7D93A7 !important;
    border-radius: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    color: #333 !important;
}
:deep(.btn:focus),
:deep(.btn:active),
:deep(.btn:hover) {
    background-color: #f5f5f5 !important;
    color: #333 !important;
}
:deep(.compact-control input) {
    padding-bottom: 5px;
    padding-top: 8px;
    margin-top: 1px;
    padding-left: 2px;
    height: 1.5rem;
}
:deep(.compact-control button) {
    height: 1.5rem;
    padding-top: 3px;
    margin-top: 1px;
}
</style>
<style>
/* Unscoped: popup is teleported to <body>, so scoped styles can't reach it.
   Bootstrap 5 modal sits at z-index 1055; bump above it so the teleported
   calendar renders on top of the modal instead of behind the backdrop. */
.calendar-popup {
    z-index: 1080;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.15);
}
</style>
